import { Fragment, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { ChevronRightThin } from "@acto/web-common/components";
import {
  Card,
  MetaLabel,
  RowBody,
  RowChevron,
  RowItem,
  RowList,
  RowMeta,
  RowTitle,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { MoreButton } from "@acto/taskpane/field-sales-2/components/primitives/MoreButton";
import { SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";

export interface RecapRow {
  id: string;
  /** Stacked day/month (previous visits) or a single time (today). */
  badge: { primary: string; secondary?: string };
  name: string;
  address: string;
}

interface RecapListProps {
  rows: RecapRow[];
  heading?: string;
  icon?: ReactNode;
  headerAction?: ReactNode;
  ctaLabel?: string;
  onRowClick?: (id: string) => void;
  onComplete?: () => void;
}

export const BucketCard = styled(Card)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[20],
  padding: `${theme.fieldSalesTwo.spacing[24]} 0`,
  background: theme.fieldSalesTwo.colors.surface.glass,
}));

export const RecapCardHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[10],
  padding: `0 ${theme.fieldSalesTwo.spacing[24]}`,
}));

export const RecapCardIcon = styled("span")(({ theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.accent.action,
  "& svg": {
    width: 22,
    height: 22,
  },
}));

export const RecapCardLabel = styled(MetaLabel)({
  flex: 1,
  minWidth: 0,
});

export const HeaderMoreButton = styled(MoreButton)(({ theme }) => ({
  height: theme.fieldSalesTwo.spacing[48],
  border: "none",
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

export const BucketBody = styled("div")(({ theme }) => ({
  padding: `0 ${theme.fieldSalesTwo.spacing[8]}`,
}));

export const ListCard = styled("div")(({ theme }) => ({
  // No token equivalent for either value.
  background: "rgba(255,255,255,0.4)",
  border: `${theme.strokeWidth.thin} solid rgba(255,255,255,0.9)`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[16]}`,
  boxSizing: "border-box",
}));

const CtaSlot = styled("div")(({ theme }) => ({
  padding: `0 ${theme.fieldSalesTwo.spacing[12]}`,
}));

export const BatchCtaButton = styled(SolidCtaButton)(({ theme }) => ({
  borderRadius: theme.fieldSalesTwo.radius.circular,
  borderColor: theme.fieldSalesTwo.colors.surface.glassBorderStrong,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.4,
  letterSpacing: 0,
}));

export const DateBadge = styled("div")(({ theme }) => ({
  flex: "0 0 auto",
  flexShrink: 0,
  width: 56,
  height: 52,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.fieldSalesTwo.radius[18],
  background: "rgba(19, 19, 19, 0.04)",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

// Keeps to the row's first text lines when the name wraps deeper; the margin optically seats
// it on the title's cap height. Scoped here: other DateBadge consumers stay centered.
const RowDateBadge = styled(DateBadge)(({ theme }) => ({
  alignSelf: "flex-start",
  marginTop: theme.fieldSalesTwo.spacing[2],
}));

// Inset divider: starts at the row text (badge width + row gap).
export const RecapRowDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.fieldSalesTwo.spacing[16],
  marginBottom: theme.fieldSalesTwo.spacing[16],
  marginLeft: theme.fieldSalesTwo.spacing[72],
  width: `calc(100% - ${theme.fieldSalesTwo.spacing[72]})`,
}));

const RecapRowList = styled(RowList)({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0,
});

/** Negative left margin lets the hover pill extend past the badge without shifting content. */
export const RecapRowItem = styled(RowItem)(({ theme }) => ({
  border: "none",
  marginLeft: `-${theme.fieldSalesTwo.spacing[8]}`,
  // important to override the top-padding reduction for the first item (need it for hover effect).
  padding: `${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]} !important`,
  borderRadius: theme.fieldSalesTwo.radius[24],
}));

export function RecapList({ rows, heading, icon, headerAction, ctaLabel, onRowClick, onComplete }: RecapListProps) {
  return (
    <BucketCard>
      {(heading || headerAction) && (
        <RecapCardHeader>
          {icon && <RecapCardIcon aria-hidden="true">{icon}</RecapCardIcon>}
          <RecapCardLabel as="h2">{heading}</RecapCardLabel>
          {headerAction}
        </RecapCardHeader>
      )}
      <BucketBody>
        <ListCard>
          <RecapRowList role="list">
            {rows.map((row, index) => (
              <Fragment key={row.id}>
                <RecapRowItem clickable onClick={() => onRowClick?.(row.id)}>
                  <RowDateBadge>
                    <span>{row.badge.primary}</span>
                    {row.badge.secondary && <span>{row.badge.secondary}</span>}
                  </RowDateBadge>
                  <RowBody>
                    <RowTitle>{row.name}</RowTitle>
                    <RowMeta>{row.address}</RowMeta>
                  </RowBody>
                  <RowChevron>
                    <ChevronRightThin strokeWidth={1.5} />
                  </RowChevron>
                </RecapRowItem>
                {index < rows.length - 1 && <RecapRowDivider />}
              </Fragment>
            ))}
          </RecapRowList>
        </ListCard>
      </BucketBody>
      {ctaLabel && onComplete && (
        <CtaSlot>
          <BatchCtaButton type="button" onClick={onComplete}>
            {ctaLabel}
          </BatchCtaButton>
        </CtaSlot>
      )}
    </BucketCard>
  );
}
